/**
 * OverlapPairDialog — why these two ETFs overlap.
 *
 * Everything the methodology publishes about a pair: overlap on the comparable
 * base, coverage of both sides, reliability, the honest bracket, the exposures
 * that build the number and — symmetrically — where each ETF invests and the
 * other does not. Both lists reconcile with what is shown: when a list is cut,
 * the hidden part is stated, not hidden.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELIABILITY = {
  high: { label: "Affidabilità alta", variant: "success" },
  usable: { label: "Affidabilità media", variant: "warning" },
  insufficient: { label: "Dati insufficienti", variant: "danger" },
  no_data: { label: "Nessun dato su questa dimensione", variant: "danger" },
};

const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${Number(value).toFixed(digits)}%`;

export function OverlapPairDialog({ open, onOpenChange, sessionId, dimension, pair }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !pair || !sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    axios
      .get(`${API}/diversification/overlap/${sessionId}/pair`, {
        params: { a: pair.a, b: pair.b, dimension },
      })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch(() => { if (!cancelled) setError("Dettaglio non disponibile."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, pair, sessionId, dimension]);

  const reliability = data ? (RELIABILITY[data.reliability] || RELIABILITY.no_data) : null;
  const truncation = data?.contributors_truncated || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-2rem)] sm:max-w-2xl bg-surface border-line text-content"
        data-testid="overlap-pair-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base" data-testid="overlap-pair-title">
            {pair ? `${pair.a} vs ${pair.b}` : "Dettaglio sovrapposizione"}
          </DialogTitle>
          <DialogDescription className="text-xs text-content-muted">
            {data?.dimension_label
              ? `Sovrapposizione strutturale per ${data.dimension_label}`
              : "Sovrapposizione strutturale"}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="px-6 pb-6 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-content-muted" data-testid="overlap-pair-loading">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Calcolo del dettaglio…
            </div>
          )}
          {error && (
            <p className="text-sm text-danger" data-testid="overlap-pair-error">{error}</p>
          )}

          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Metric
                  testId="overlap-pair-value"
                  label="Sovrapposizione"
                  value={pct(data.classified_overlap_pct)}
                  hint="sulla parte classificata"
                />
                <Metric
                  testId="overlap-pair-bracket"
                  label="Intervallo reale"
                  value={`${pct(data.true_min_pct, 1)} – ${pct(data.true_max_pct, 1)}`}
                  hint="con i dati mancanti"
                />
                <Metric
                  testId="overlap-pair-coverage-a"
                  label={`Coverage ${data.subject_a}`}
                  value={pct(data.coverage_a_pct, 1)}
                />
                <Metric
                  testId="overlap-pair-coverage-b"
                  label={`Coverage ${data.subject_b}`}
                  value={pct(data.coverage_b_pct, 1)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={reliability.variant} data-testid="overlap-pair-reliability">
                  {reliability.label}
                </Badge>
                {!data.supported && (
                  <span className="text-label text-content-muted">
                    Il valore non è abbastanza supportato dai dati: leggi coverage e intervallo.
                  </span>
                )}
                {(data.flags || []).map((flag) => (
                  <Badge key={flag} variant="warning" data-testid={`overlap-pair-flag-${flag}`}>
                    <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                    {flag === "net_short_exposure"
                      ? "posizioni short/offset rilevanti"
                      : "pesi del file non coerenti"}
                  </Badge>
                ))}
              </div>

              {data.contributors?.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-content-secondary">
                    Dove si sovrappongono
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-line">
                    <table className="w-full text-label" data-testid="overlap-contributors">
                      <thead>
                        <tr className="bg-surface-subtle text-content-muted">
                          <th scope="col" className="px-3 py-2 text-left font-medium">Esposizione</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">{data.subject_a}</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">{data.subject_b}</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Contributo</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Quota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.contributors.map((c) => (
                          <tr
                            key={c.key}
                            className="border-t border-line"
                            data-testid={`overlap-contributor-${c.key}`}
                          >
                            <td className="px-3 py-2">
                              <span className="block text-content">{c.label}</span>
                              {c.label !== c.key && (
                                <span className="block text-label font-mono text-content-muted">
                                  {c.key}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular">{pct(c.weight_a_pct, 2)}</td>
                            <td className="px-3 py-2 text-right tabular">{pct(c.weight_b_pct, 2)}</td>
                            <td className="px-3 py-2 text-right tabular font-medium">{pct(c.contribution_pct, 2)}</td>
                            <td className="px-3 py-2 text-right tabular text-content-muted">{pct(c.share_of_overlap_pct, 1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-label text-content-muted" data-testid="overlap-contributors-reconciliation">
                    {truncation
                      ? `Mostrate ${truncation.shown} esposizioni su ${truncation.total}: `
                        + `${pct(truncation.shown_contribution_pct, 2)} dei ${pct(data.classified_overlap_pct, 2)} `
                        + `di sovrapposizione, ${pct(truncation.hidden_contribution_pct, 2)} non visualizzati.`
                      : `Le ${data.contributors.length} esposizioni mostrate spiegano tutta la sovrapposizione `
                        + `(${pct(data.classified_overlap_pct, 2)}).`}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-content-muted" data-testid="overlap-contributors-empty">
                  Nessuna esposizione in comune su questa dimensione.
                </p>
              )}

              {(data.only_a || data.only_b) && (
                <div className="space-y-2" data-testid="overlap-differentiators">
                  <h3 className="text-sm font-medium text-content-secondary">
                    Dove non si sovrappongono
                  </h3>
                  <p className="text-label text-content-muted" data-testid="overlap-identity">
                    Sulla parte classificata di ciascun ETF:{" "}
                    <strong className="text-content">{pct(data.classified_overlap_pct, 2)}</strong>{" "}
                    in comune +{" "}
                    <strong className="text-content">{pct(data.only_a?.total_pct, 2)}</strong>{" "}
                    esclusivo = 100%. La parte esclusiva pesa uguale sui due lati, ma è
                    composta da esposizioni diverse.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ExclusiveSide side={data.only_a} />
                    <ExclusiveSide side={data.only_b} />
                  </div>
                </div>
              )}

              <p className="text-label text-content-muted" data-testid="overlap-pair-note">
                La sovrapposizione è strutturale, non è una correlazione: 0% significa
                nessuna esposizione condivisa su questa dimensione, non assenza di
                rischio comune.
              </p>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function ExclusiveSide({ side }) {
  if (!side) return null;
  const truncated = side.truncated;
  return (
    <div
      className="rounded-lg border border-line overflow-hidden"
      data-testid={`overlap-only-${side.subject}`}
    >
      <div className="flex items-baseline justify-between gap-2 bg-surface-subtle px-3 py-2">
        <h4 className="text-label font-medium text-content-secondary">
          Solo {side.subject}
        </h4>
        <span className="text-label tabular font-semibold text-content">
          {pct(side.total_pct, 2)}
        </span>
      </div>
      {side.items.length === 0 ? (
        <p
          className="px-3 py-2 text-label text-content-muted"
          data-testid={`overlap-only-${side.subject}-empty`}
        >
          Nessuna esposizione esclusiva: {side.subject} è interamente contenuto
          nell&apos;altro ETF su questa dimensione.
        </p>
      ) : (
        <table className="w-full text-label">
          <thead>
            <tr className="text-content-muted border-b border-line">
              <th scope="col" className="px-3 py-1.5 text-left font-medium">Esposizione</th>
              <th scope="col" className="px-3 py-1.5 text-right font-medium">In {side.subject}</th>
              <th scope="col" className="px-3 py-1.5 text-right font-medium">Esclusivo</th>
            </tr>
          </thead>
          <tbody>
            {side.items.map((item) => (
              <tr
                key={item.key}
                className="border-t border-line"
                data-testid={`overlap-only-${side.subject}-${item.key}`}
              >
                <td className="px-3 py-1.5">
                  <span className="block text-content">{item.label}</span>
                  {item.label !== item.key && (
                    <span className="block text-label font-mono text-content-muted">
                      {item.key}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular">{pct(item.weight_own_pct, 2)}</td>
                <td className="px-3 py-1.5 text-right tabular font-medium">
                  {pct(item.exclusive_pct, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {side.items.length > 0 && (
        <p
          className="px-3 py-2 text-label text-content-muted border-t border-line"
          data-testid={`overlap-only-${side.subject}-reconciliation`}
        >
          {truncated
            ? `Mostrate ${truncated.shown} esposizioni su ${truncated.total}: `
              + `${pct(truncated.shown_contribution_pct, 2)} dei ${pct(side.total_pct, 2)} `
              + `esclusivi, ${pct(truncated.hidden_contribution_pct, 2)} non visualizzati.`
            : `Elenco completo: le ${side.items.length} esposizioni mostrate spiegano `
              + `tutti i ${pct(side.total_pct, 2)} esclusivi.`}
        </p>
      )}
    </div>
  );
}

function Metric({ label, value, hint, testId }) {
  return (
    <div className="rounded-lg border border-line bg-surface-subtle px-3 py-2" data-testid={testId}>
      <span className="block text-label uppercase tracking-wide text-content-muted">{label}</span>
      <span className="block text-sm font-semibold tabular text-content">{value}</span>
      {hint && <span className="block text-label text-content-muted">{hint}</span>}
    </div>
  );
}

export default OverlapPairDialog;
